import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import useClock from '../hooks/useClock';
import useUserIdentity from '../hooks/useUserIdentity';

import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import ListItem from '@mui/joy/ListItem';
import ListItemButton from '@mui/joy/ListItemButton';
import ListItemContent from '@mui/joy/ListItemContent';
import Tooltip from '@mui/joy/Tooltip';
import Accordion from '@mui/joy/Accordion';
import AccordionDetails from '@mui/joy/AccordionDetails';
import AccordionSummary from '@mui/joy/AccordionSummary';
import AccordionGroup from '@mui/joy/AccordionGroup';
import Button from '@mui/joy/Button';

import { ClockProps } from '../stores/clock_store';
import IdentityAvatar from 'components/IdentityAvatar';

import { TimeUI } from './Date';

import { getIcon, pluralize, timeDuration } from 'utils/misc';
import RoleStack from './RoleStack';
import { ClockSupportOptions } from 'components/CreditLog';
import useRoles from 'hooks/useRoles';

const ClockItem = observer(({ clock }: { clock: ClockProps }) => {

    const { hasRole } = useRoles();
    const [ diff, setDiff ] = useState(0);

    const updateDiff = useCallback(() => {
        const d = clock.time_end ? new Date(clock.time_end).getTime() : new Date().getTime();
        const s = new Date(clock.time_start).getTime();
        setDiff(d  - s)
    }, [clock.time_end, clock.time_start]);

    useEffect(() => {
        updateDiff()
        if(!clock.time_end) {
            setInterval(updateDiff, 1_000);
        }
    }, [updateDiff, clock.time_end]);

    return (
        <ListItem>
            <ListItemButton>
                <ListItemContent>
                    <Stack>

                    <Stack direction="row" spacing={1}>
                        <Stack>
                            <Typography level="body-sm">
                                <TimeUI ts={clock.time_start} /> - <TimeUI showDate={false} ts={clock.time_end} />
                            </Typography>
                        </Stack>
                    </Stack>
                        <Typography level="body-xs">

                        <IdentityAvatar
                            size="name"
                            identityKey={clock.identity} />
                        </Typography>
                    </Stack>
                </ListItemContent>
                <Tooltip arrow title="More or less">
                    <Typography
                        level="body-sm">{timeDuration(Math.floor(diff / 1_000))}
                    </Typography>
                    </Tooltip>
                    { hasRole('manager') &&
                        <ClockSupportOptions narrow={true} clock={clock} />
                    }
            </ListItemButton>
        </ListItem>

    );
});


const CollabClockTower = observer(({ associated_type, associated_uuid }: { associated_type: string, associated_uuid: string }) => {
    const {
        fetchClocksForCollab, getClocksForCollab,
        fetchClocksForRequest, getClocksForRequest,
        resetTotals
    } = useClock();
    const { currentIdentity } = useUserIdentity();
    const [resetting, setResetting] = useState(false);
    const PunchClock = getIcon('PunchClock');

    useEffect(() => {
        if(!currentIdentity?.key) return;
        if(associated_type == "collab") {
            fetchClocksForCollab(associated_uuid, currentIdentity.key);
        }
        if(associated_type == "request") {
            fetchClocksForRequest(associated_uuid, currentIdentity.key);
        }
    }, [associated_type, associated_uuid, currentIdentity?.key]);

    function doResetTotals() {

        if(currentIdentity?.uuid) {
            setResetting(true);
            resetTotals(currentIdentity.uuid, associated_type, associated_uuid)
            .finally(() => setResetting(false));
        }
    }

    const clocks = associated_type == 'collab' ?
        getClocksForCollab(associated_uuid) :
        getClocksForRequest(associated_uuid);

    return (

        <AccordionGroup size="lg">
            <Accordion>
                <AccordionSummary>
                    <PunchClock />

                    <Typography level="body-sm">
                        {pluralize(clocks?.length, 'Clock')}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                <RoleStack role="assistant" spacing={1} direction="row" justifyContent="center">

                <Button size="sm" onClick={doResetTotals} disabled={resetting} variant="outlined">Update Totals</Button>
                <Link to="clocks"><Button size="sm" variant="outlined">View Report</Button></Link>

                    </RoleStack>
                    {clocks &&
                        clocks.map((c) =>
                            <ClockItem key={c.key} clock={c} />
                        )
                    }
                </AccordionDetails>
            </Accordion>
        </AccordionGroup>

    );

});

export default CollabClockTower;
